import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Reservations.css';
import '../../components/common/IconButton.css';
import Modal from '../../components/common/Modal';
import {
  getFile,
  updateFile,
  createServiceForFile,
  updateService,
  deleteService,
  createPaymentForFile,
  updatePayment,
  deletePayment,
  addPassengerToFile,
  removePassengerFromFile
} from '../../services/fileService';
import { getOperators } from '../../services/operatorService';
import { getServiceDocumentUrl, getServiceDocuments, uploadServiceDocument } from '../../services/serviceService';
import ServiceModal from '../../components/modals/ServiceModal';
import PaymentModal from '../../components/modals/PaymentModal';
import PassengerModal from '../../components/modals/PassengerModal';
import Fallback from '../../components/common/Fallback';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { IconArrowRight, IconFile, IconPencil, IconPrinter, IconTrash } from '../../components/common/Icons';
import { formatDate } from '../../utils/date';
import { digitsOnly, formatDni } from '../../utils/formatters';

const sumByCurrency = (items, field) => {
  return items.reduce((acc, item) => {
    const currency = item.currency || '$';
    const value = Number(item[field] || 0);
    acc[currency] = (acc[currency] || 0) + value;
    return acc;
  }, {});
};

const FilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [operators, setOperators] = useState([]);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [dateOut, setDateOut] = useState('');
  const [dateIn, setDateIn] = useState('');
  const [dateError, setDateError] = useState('');
  const [savingDates, setSavingDates] = useState(false);
  const [passengerModalOpen, setPassengerModalOpen] = useState(false);
  const [passengerBusy, setPassengerBusy] = useState(false);
  const [passengerEditId, setPassengerEditId] = useState(null);
  const [serviceDeleteConfirmOpen, setServiceDeleteConfirmOpen] = useState(false);
  const [pendingDeleteService, setPendingDeleteService] = useState(null);
  const [receiptSelectorOpen, setReceiptSelectorOpen] = useState(false);
  const [selectedReceiptPaymentIds, setSelectedReceiptPaymentIds] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const data = await getFile(id);
      const servicesWithDocumentStatus = await Promise.all(
        (data.services || []).map(async (service) => {
          try {
            const documents = await getServiceDocuments(service.id);
            return {
              ...service,
              has_reservation_document: Array.isArray(documents)
                ? documents.some((document) => document.document_type === 'reservation')
                : false
            };
          } catch (error) {
            console.error(`No se pudieron verificar los documentos del servicio ${service.id}:`, error);
            return {
              ...service,
              has_reservation_document: Number(service.has_reservation_document) === 1
            };
          }
        })
      );
      setFile({ ...data, services: servicesWithDocumentStatus });
      // dateIn = salida, dateOut = vuelta
      setDateIn(data.date_in ? data.date_in.slice(0, 10) : data.dateIn || '');
      setDateOut(data.date_out ? data.date_out.slice(0, 10) : data.dateOut || '');
    } catch (err) {
      console.error(err);
      if (err?.message?.toLowerCase().includes('not found')) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    getOperators().then(setOperators).catch((err) => console.error(err));
  }, []);

  const serviceTotals = useMemo(() => file ? sumByCurrency(file.services || [], 'price') : {}, [file]);
  const paymentTotals = useMemo(() => file ? sumByCurrency(file.payments || [], 'value') : {}, [file]);
  const saldo = useMemo(() => {
    const result = { ...serviceTotals };
    Object.entries(paymentTotals).forEach(([currency, val]) => {
      result[currency] = (result[currency] || 0) - val;
    });
    return result;
  }, [serviceTotals, paymentTotals]);

  const filePayments = useMemo(() => {
    const fromApi = file?.payments || [];
    if (!Array.isArray(fromApi)) return [];

    const toTime = (d) => {
      if (!d) return Number.POSITIVE_INFINITY;
      const t = Date.parse(d);
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };

    return [...fromApi].sort((a, b) => {
      const aTime = toTime(a?.date);
      const bTime = toTime(b?.date);
      if (aTime !== bTime) return aTime - bTime;

      // desempate estable
      const aId = Number(a?.id) || 0;
      const bId = Number(b?.id) || 0;
      return aId - bId;
    });
  }, [file]);

  const openReceiptSelector = () => {
    setSelectedReceiptPaymentIds(filePayments.map((p) => Number(p.id)).filter((n) => !Number.isNaN(n)));
    setReceiptSelectorOpen(true);
  };

  const toggleReceiptPayment = (paymentId) => {
    setSelectedReceiptPaymentIds((prev) => {
      if (prev.includes(paymentId)) return prev.filter((idToKeep) => idToKeep !== paymentId);
      return [...prev, paymentId];
    });
  };

  const handleGenerateReceipt = () => {
    if (!selectedReceiptPaymentIds.length) return;
    const paymentIds = selectedReceiptPaymentIds.join(',');
    window.open(`/backoffice/ficha/${id}/recibo?paymentIds=${encodeURIComponent(paymentIds)}`, '_blank', 'noopener,noreferrer');
    setReceiptSelectorOpen(false);
  };

  const handleSaveService = async (data) => {
    let savedService;
    if (selectedService) {
      savedService = await updateService(selectedService.id, {
        ...data,
        operatorId: Number(data.operatorId),
        price: Number(data.price || 0),
        conversion: data.currency === '$' && data.conversion !== '' && data.conversion !== null && data.conversion !== undefined
          ? Number(data.conversion)
          : null,
        dateOut: data.dateOut,
        dateIn: data.dateIn || null
      });
    } else {
      savedService = await createServiceForFile(id, {
        ...data,
        operatorId: Number(data.operatorId),
        price: Number(data.price || 0),
        conversion: data.currency === '$' && data.conversion !== '' && data.conversion !== null && data.conversion !== undefined
          ? Number(data.conversion)
          : null,
        dateOut: data.dateOut,
        dateIn: data.dateIn || null
      });
    }
    if (data.reservationDocument) {
      const serviceId = savedService?.id || selectedService?.id;
      if (!serviceId) throw new Error('No se pudo identificar el servicio para adjuntar la Reserva.');
      await uploadServiceDocument(serviceId, 'reservation', data.reservationDocument);
    }
    setServiceModalOpen(false);
    setSelectedService(null);
    await loadData();
  };

  const handleDeleteService = async (serviceId) => {
    await deleteService(serviceId);
    setServiceModalOpen(false);
    setSelectedService(null);
    await loadData();
  };

  const handleSavePayment = async (data) => {
    if (selectedPayment) {
      await updatePayment(selectedPayment.id, data);
    } else {
      await createPaymentForFile(id, data);
    }
    setPaymentModalOpen(false);
    setSelectedPayment(null);
    await loadData();
  };

  const handleDeletePayment = async (paymentId) => {
    await deletePayment(paymentId);
    setPaymentModalOpen(false);
    setSelectedPayment(null);
    await loadData();
  };

  const filePassengers = useMemo(() => {
    const fromApi = file?.passengers || [];
    if (!Array.isArray(fromApi)) return [];
    return [...fromApi].sort((a, b) => {
      const aId = Number(a?.id) || 0;
      const bId = Number(b?.id) || 0;
      return aId - bId;
    });
  }, [file]);

  const fileServices = useMemo(() => {
    const fromApi = file?.services || [];
    if (!Array.isArray(fromApi)) return [];
    return [...fromApi].sort((a, b) => {
      const aId = Number(a?.id) || 0;
      const bId = Number(b?.id) || 0;
      return aId - bId;
    });
  }, [file]);

  const associatedPassengerIds = useMemo(
    () => filePassengers.map((p) => Number(p.id)).filter((x) => !Number.isNaN(x)),
    [filePassengers]
  );

  const clientForPassengerModal = useMemo(() => {
    if (!file) return null;
    return {
      id: file.client_id,
      dni: file.client_dni,
      name: file.client_name,
      surname: file.client_surname,
      birthday: file.client_birthday,
      mail: file.client_mail,
    };
  }, [file]);

  const handleAddPassengerById = async (passengerId) => {
    if (!passengerId) return;
    setPassengerBusy(true);
    try {
      await addPassengerToFile(id, Number(passengerId));
      await loadData();
    } finally {
      setPassengerBusy(false);
    }
  };

  const handleRemovePassenger = async (passengerId) => {
    setPassengerBusy(true);
    try {
      await removePassengerFromFile(id, Number(passengerId));
      await loadData();
    } finally {
      setPassengerBusy(false);
    }
  };

  const originalDateIn = useMemo(() => {
    if (!file) return '';
    return file.date_in ? file.date_in.slice(0, 10) : (file.dateIn || '');
  }, [file]);

  const originalDateOut = useMemo(() => {
    if (!file) return '';
    return file.date_out ? file.date_out.slice(0, 10) : (file.dateOut || '');
  }, [file]);

  const datesAreValid = useMemo(() => {
    if (!dateIn || !dateOut) return true;
    // YYYY-MM-DD is lexicographically comparable
    return dateOut >= dateIn;
  }, [dateIn, dateOut]);

  const datesChanged = useMemo(() => {
    return dateIn !== originalDateIn || dateOut !== originalDateOut;
  }, [dateIn, dateOut, originalDateIn, originalDateOut]);

  const handleUpdateFileInfo = async () => {
    setDateError('');
    if (dateIn && dateOut && dateOut < dateIn) {
      setDateError('La fecha de vuelta debe ser mayor o igual que la fecha de salida.');
      return;
    }
    if (!datesChanged) return;
    setSavingDates(true);
    try {
      await updateFile(id, {
        clientId: file.client_id,
        creationDate: file.creation_date,
        dateIn,
        dateOut,
        status: file.status
      });
      await loadData();
    } finally {
      setSavingDates(false);
    }
  };

  if (loading || !file) {
    return (
      <div className="page-wrapper">
        {loading && <p>Cargando ficha...</p>}
        {!loading && notFound && (
          <Fallback
            title="Ficha no encontrada"
            description="La ficha solicitada no existe."
          />
        )}
        {!loading && !notFound && <Fallback title="Error" description="Error al cargar la ficha." />}
      </div>
    );
  }

  return (
    <div className="file-page">
      <div className="file-header-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="file-title">Ficha de reserva #{file.id}</div>
          <button
            type="button"
            className="icon-btn icon-btn--print"
            onClick={() => window.open(`/backoffice/ficha/${id}/print`, '_blank', 'noopener,noreferrer')}
            aria-label="Generar ficha de reserva"
            title="Generar ficha de reserva"
          >
            <IconPrinter />
          </button>
        </div>
        <div className="file-status-card">
          <div className="file-status-label">Estado</div>
          <div
            className={`file-status-value file-status-value--regular ${
              file.status === 'Completo'
                ? 'completo'
                : file.status === 'Deprecado'
                  ? 'deprecado'
                  : 'incompleto'
            }`}
          >
            {file.status || 'Incompleto'}
          </div>
          <div className="file-status-sub" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>Fecha de alta:</span>
            <span>{formatDate(file.creation_date ?? file.creationDate)}</span>
          </div>
        </div>
      </div>
      <div className="file-client-row">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="file-client-label">Cliente:</span>
          <span>{file.client_surname}, {file.client_name}</span>
        </div>

        <div className="file-client-actions" style={{ marginLeft: 0, width: '100%' }}>
          <span className="file-client-label">Salida:</span>
          <input
            type="date"
            value={dateIn}
            onChange={(e) => {
              setDateIn(e.target.value);
              setDateError('');
            }}
            className="file-date-input"
            disabled={savingDates}
          />
          <span className="file-client-label">Vuelta:</span>
          <input
            type="date"
            value={dateOut}
            onChange={(e) => {
              setDateOut(e.target.value);
              setDateError('');
            }}
            className="file-date-input"
            min={dateIn || undefined}
            disabled={savingDates}
          />
          <button
            className="file-add-btn"
            onClick={handleUpdateFileInfo}
            disabled={savingDates || !datesChanged || !datesAreValid}
            title={!datesChanged ? 'No hay cambios para guardar' : (!datesAreValid ? 'Fechas inválidas' : undefined)}
          >
            {savingDates ? 'Actualizando...' : 'Actualizar fecha'}
          </button>
          {dateError && <div className="form-error-message form-error-message--inline" role="alert">{dateError}</div>}
        </div>
      </div>

      <div className="file-section">
        <div className="file-section-title">
          <span>Pasajeros</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn"
              onClick={() => {
                setPassengerEditId(null);
                setPassengerModalOpen(true);
              }}
              disabled={!clientForPassengerModal?.id}
              title="Seleccionar pasajero para asociar a la ficha"
            >
              + Asociar a la ficha
            </button>
          </div>
        </div>

        {filePassengers.length === 0 ? (
          <p className="empty-state">No hay pasajeros asociados a esta ficha aún.</p>
        ) : (
          <table className="file-table">
            <thead>
              <tr>
                <th>Apellido</th>
                <th>Nombre</th>
                <th>DNI</th>
                <th>Nacimiento</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filePassengers.map((p) => (
                <tr key={p.id}>
                  <td>{p.surname || '-'}</td>
                  <td>{p.name || '-'}</td>
                  <td>{formatDni(p.dni) || '-'}</td>
                  <td>{formatDate(p.birthday)}</td>
                  <td>
                    {(() => {
                      const isClientPassenger =
                        digitsOnly(p?.dni) &&
                        digitsOnly(clientForPassengerModal?.dni) &&
                        digitsOnly(p?.dni) === digitsOnly(clientForPassengerModal?.dni);

                      return (
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="icon-btn icon-btn--edit"
                        onClick={() => {
                          setPassengerEditId(p.id);
                          setPassengerModalOpen(true);
                        }}
                        disabled={passengerBusy || isClientPassenger}
                        aria-label="Editar pasajero"
                        title={isClientPassenger ? 'No se puede editar el pasajero principal (cliente)' : 'Editar'}
                      >
                        <IconPencil />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--delete"
                        onClick={() => handleRemovePassenger(p.id)}
                        disabled={passengerBusy}
                        aria-label="Quitar pasajero"
                        title="Quitar"
                      >
                        <IconTrash />
                      </button>
                    </div>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="file-section">
        <div className="file-section-title">
          <span>Servicios</span>
          <button className="btn" onClick={() => { setSelectedService(null); setServiceModalOpen(true); }}>+ Agregar servicio</button>
        </div>
        {(!file.services || file.services.length === 0) ? (
          <p className="empty-state">No hay servicios cargados aún.</p>
        ) : (
          <table className="file-table">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Tipo</th>
                <th>Operador</th>
                <th>Monto</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {fileServices.map((s) => (
                <tr key={s.id}>
                  <td>{s.description || '-'}</td>
                  <td>{s.type}</td>
                  <td>{s.operator_name}</td>
                  <td>{`${s.currency || '$'} ${Number(s.price || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`}</td>
                  <td>
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="icon-btn icon-btn--edit"
                        onClick={() => { setSelectedService(s); setServiceModalOpen(true); }}
                        aria-label="Editar servicio"
                        title="Editar"
                      >
                        <IconPencil />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--delete"
                        onClick={() => {
                          setPendingDeleteService(s);
                          setServiceDeleteConfirmOpen(true);
                        }}
                        aria-label="Eliminar servicio"
                        title="Eliminar"
                      >
                        <IconTrash />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--select"
                        onClick={() => window.open(getServiceDocumentUrl(s.id, 'reservation'), '_blank', 'noopener,noreferrer')}
                        disabled={Number(s.has_reservation_document) !== 1}
                        aria-label="Abrir documento de reserva"
                        title={Number(s.has_reservation_document) === 1 ? 'Abrir reserva' : 'Sin reserva cargada'}
                      >
                        <IconFile />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--select"
                        onClick={() => navigate(`/backoffice/servicio/${s.id}`)}
                        aria-label="Ver detalles"
                        title="Ver detalles"
                      >
                        <IconArrowRight />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="file-totals-box">
          <span className="file-total-label">TOTAL</span>
          <div className="file-saldo-box">
            {Object.entries(serviceTotals).map(([currency, val], idx) => (
              <div key={currency} className="file-saldo-item" style={idx === 0 ? { borderTop: 'none' } : {}}>
                {currency} {val.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="file-section">
        <div className="file-section-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Pagos</span>
            <button
              type="button"
              className="icon-btn icon-btn--print"
              onClick={openReceiptSelector}
              disabled={filePayments.length === 0}
              aria-label="Generar recibo"
              title="Generar recibo"
            >
              <IconPrinter />
            </button>
          </div>
          <button className="btn" onClick={() => { setSelectedPayment(null); setPaymentModalOpen(true); }}>+ Agregar pago</button>
        </div>
        {(!file.payments || file.payments.length === 0) ? (
          <p className="empty-state">No hay pagos cargados aún.</p>
        ) : (
          <table className="file-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Detalle</th>
                <th>Monto</th>
                <th>Comprobante</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filePayments.map((p) => (
                <tr key={p.id}>
                  <td>{formatDate(p.date)}</td>
                  <td>{p.type || '-'}</td>
                  <td>{(p.detail || '-').toString()}</td>
                  <td>{`${p.currency || '$'} ${Number(p.value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`}</td>
                  <td>{p.receipt || '-'}</td>
                  <td>
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="icon-btn icon-btn--edit"
                        onClick={() => { setSelectedPayment(p); setPaymentModalOpen(true); }}
                        aria-label="Editar pago"
                        title="Editar"
                      >
                        <IconPencil />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--delete"
                        onClick={() => handleDeletePayment(p.id)}
                        aria-label="Eliminar pago"
                        title="Eliminar"
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="file-totals-box">
          <span className="file-total-label">TOTAL</span>
          <div className="file-saldo-box">
            {Object.entries(paymentTotals).map(([currency, val], idx) => (
              <div key={currency} className="file-saldo-item" style={idx === 0 ? { borderTop: 'none' } : {}}>
                {currency} {val.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="file-saldo">
        <span>SALDO</span>
        <div className="file-saldo-box">
          {Object.entries(saldo).map(([currency, val], idx) => (
            <div key={currency} className="file-saldo-item" style={idx === 0 ? { borderTop: 'none' } : {}}>
              {currency} {val.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </div>
          ))}
        </div>
      </div>

      <ServiceModal
        isOpen={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        onSave={handleSaveService}
        initialData={selectedService}
        operators={operators}
        defaultDateOut={dateOut}
        defaultDateIn={dateIn}
      />

      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSave={handleSavePayment}
        onDelete={selectedPayment ? () => handleDeletePayment(selectedPayment.id) : undefined}
        initialData={selectedPayment}
      />

      <PassengerModal
        isOpen={passengerModalOpen}
        onClose={() => {
          setPassengerModalOpen(false);
          setPassengerEditId(null);
        }}
        client={clientForPassengerModal}
        mode="select"
        excludePassengerIds={associatedPassengerIds}
        autoEditPassengerId={passengerEditId}
        onSelectPassenger={async (p) => {
          await handleAddPassengerById(p.id);
          setPassengerModalOpen(false);
          setPassengerEditId(null);
        }}
      />

      <ConfirmDialog
        isOpen={serviceDeleteConfirmOpen}
        title="Eliminar servicio"
        message={`¿Está seguro que desea eliminar este servicio${pendingDeleteService?.description ? `: "${pendingDeleteService.description}"` : ''}?`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        onConfirm={async () => {
          if (!pendingDeleteService?.id) return;
          await handleDeleteService(pendingDeleteService.id);
          setServiceDeleteConfirmOpen(false);
          setPendingDeleteService(null);
        }}
        onCancel={() => {
          setServiceDeleteConfirmOpen(false);
          setPendingDeleteService(null);
        }}
      />

      <Modal
        title="Seleccionar pagos para recibo"
        isOpen={receiptSelectorOpen}
        onClose={() => setReceiptSelectorOpen(false)}
        footer={filePayments.length > 0 ? (
          <div className="modal-footer">
            <div className="footer-left" />
            <div className="footer-actions receipt-selector-actions">
              <button type="button" className="btn secondary receipt-selector-btn" onClick={() => setReceiptSelectorOpen(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn receipt-selector-btn"
                onClick={handleGenerateReceipt}
                disabled={selectedReceiptPaymentIds.length === 0}
                style={{ background: '#1d4ed8', color: '#fff', borderColor: '#1d4ed8' }}
              >
                Generar recibo
              </button>
            </div>
          </div>
        ) : null}
      >
        {filePayments.length === 0 ? (
          <p className="empty-state">No hay pagos cargados aún.</p>
        ) : (
          <div style={{ maxHeight: 320, overflow: 'auto', marginBottom: 16 }}>
            <table className="file-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Detalle</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filePayments.map((p) => {
                  const paymentId = Number(p.id);
                  const isChecked = selectedReceiptPaymentIds.includes(paymentId);
                  return (
                    <tr key={p.id}>
                      <td>{formatDate(p.date)}</td>
                      <td>{p.type || '-'}</td>
                      <td>{(p.detail || '-').toString()}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleReceiptPayment(paymentId)}
                          aria-label={`Seleccionar pago ${p.id}`}
                          title="Seleccionar pago para recibo"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FilePage;
